import { BusinessSociabilitySaysApproved } from "common/types";
import { useAuth, useI18N, usePostNoteDelete } from "common/hooks";
import { useEffect, useState } from "react";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import ModalWithAction from "@/components/common/ModalWithAction";

type VenueNoteView = {
  key: number;
  note: BusinessSociabilitySaysApproved;
  onRefresh: () => void;
  onEdit: () => void;
  onFlagNote: () => void;
};

function VenueNoteView({
  key,
  note,
  onRefresh,
  onEdit,
  onFlagNote,
}: VenueNoteView) {
  const { isMapper } = useAuth();
  const isDark = useDarkMode();
  const i18n = useI18N();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    data: deleteResponse,
    error,
    mutate: deleteNote,
    status,
  } = usePostNoteDelete();
  const showToast = useCustomToast();

  useEffect(() => {
    if (deleteResponse) {
      console.log(`deleteNote data: ${deleteResponse.message}`);
      setIsModalOpen(false);
      showToast(deleteResponse.message, "success");
      setTimeout(() => {
        onRefresh();
      }, 1000);
    }
  }, [deleteResponse]);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error]);

  return (
    <div
      key={key}
      className="mb-2 border border-primary-200 rounded-xl p-4 h-full flex flex-col"
    >
      <div className="self-stretch min-w-11 min-h-11 py-2 inline-flex justify-center items-start gap-4 flex-wrap content-start">
        <div className="flex-1 min-h-6 inline-flex flex-col justify-center items-start">
          <div className="self-stretch justify-center ucfirst text-primary-800 dark:text-primary-300 paragraph-medium-semi-bold">
            {note.business_note_title}
          </div>
          {(note.schedule_date_from && note.schedule_date_to) ||
          note.schedule_date_from ? (
            <div className="self-stretch justify-center text-neutral-700 paragraph-xsmall-medium">
              {note.schedule_date_from && note.schedule_date_to
                ? `${note.formatedScheduleDateFrom} - ${note.formatedScheduleDateTo}`
                : `${note.formatedScheduleDateFrom} - No end date`}
            </div>
          ) : null}
        </div>
        <div className="flex justify-start items-center">
          {isMapper && (
            <div className="flex flex-row gap-2">
              <button
                disabled={status === "pending"}
                onClick={() => {
                  onEdit();
                }}
                className="w-10 h-10 relative bg-primary-50 dark:bg-[#313A71] border border-primary-200 rounded-lg flex items-center justify-center hover:bg-primary-100 dark:hover:bg-[#3F4A8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-5 h-5">
                  <img
                    src={
                      isDark
                        ? "/images/PencilSimple_dark.svg"
                        : "/images/PencilSimple_Primary500.svg"
                    }
                    alt="Edit note"
                    className="w-full h-full"
                  />
                </div>
              </button>

              <button
                disabled={status === "pending"}
                onClick={() => {
                  setIsModalOpen(true);
                }}
                className="w-10 h-10 relative bg-primary-50 dark:bg-[#313A71] border border-primary-200 rounded-lg flex items-center justify-center hover:bg-primary-100 dark:hover:bg-[#3F4A8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-5 h-5">
                  <img
                    src={
                      isDark ? "/images/Trash_red.svg" : "/images/Trash_red.svg"
                    }
                    alt="Delete note"
                    className="w-full h-full"
                  />
                </div>
              </button>
            </div>
          )}

          {!isMapper && (
            <div className="flex flex-row gap-2">
              <button
                disabled={status === "pending"}
                onClick={() => {
                  onFlagNote();
                }}
                className="w-10 h-10 relative bg-primary-50 dark:bg-[#313A71] border border-primary-200 rounded-lg flex items-center justify-center hover:bg-primary-100 dark:hover:bg-[#3F4A8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-5 h-5">
                  <img
                    src={
                      isDark ? "/images/Flag_red.svg" : "/images/Flag_red.svg"
                    }
                    alt="Flag note"
                    className="w-full h-full"
                  />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="self-stretch  flex flex-col justify-start items-start gap-2.5 border border-primary-150"></div>
      <div className="self-stretch py-2 inline-flex justify-start items-center gap-4">
        <div className="flex-1 min-h-6 inline-flex flex-col justify-center items-start">
          <div className="self-stretch justify-center">
            <span className="text-primary-800 dark:text-primary-300 paragraph-medium-semi-bold">
              Location :
            </span>
            <span className="text-primary-800 dark:text-primary-300 paragraph-small-medium">
              {" "}
              {note.location_name}
            </span>
          </div>
        </div>
      </div>
      
      <div className="self-stretch flex flex-col justify-start items-start gap-2.5 border border-primary-150"></div>
      
      <div className="self-stretch min-w-11 min-h-11 py-2 flex flex-col justify-center items-center gap-1">
        <div className="self-stretch justify-center text-neutral-700 dark:text-primary-300 paragraph-xsmall-medium">
          {note.formatedActionDoneAt} • {isMapper ? note.business_note_by_user : "Sociability team"}
        </div>  
        
        {note.business_note_text && (
          <div className="self-stretch min-h-6 flex flex-col justify-center items-start paragraph-small-medium text-primary-800 dark:text-primary-300">
            <div className="self-stretch justify-center ucfirst paragraph-small-medium text-primary-800 dark:text-primary-300">
              {note.business_note_text}
            </div>
          </div>
        )}
      </div>

      {status === "pending" && <Loader />}

      <ModalWithAction
        isModalOpen={isModalOpen}
        title={i18n.get("deleteNote")}
        subtitle={i18n.get("deleteNoteMessage")}
        cancelButtonTitle={i18n.get("cancel")}
        actionButtonTitle={i18n.get("deleteNote")}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onClick={() => {
          deleteNote({ noteId: note.business_note_text_id.toString() });
        }}
        type="warning"
        actionButtonType="warning"
      />
    </div>
  );
}

export default VenueNoteView;
